import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Play, Save, BookOpen } from "lucide-react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { StatusBadge } from "@/pages/platforms/SuiviDepot";
import PdfViewer from "@/components/PdfViewer";

const STATUSES = [
  "Dépôt reçu",
  "Contrôle administratif",
  "Test anti-plagiat",
  "En cours d'analyse",
  "Validé",
  "Validé sous réserve",
  "À corriger",
  "Refusé",
  "Archivé",
];

export default function DepositDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [showViewer, setShowViewer] = useState(false);

  const load = () => api.get(`/deposits/${id}`).then((r) => { setDoc(r.data); setStatus(r.data.status); });
  useEffect(() => { load(); }, [id]);

  const saveStatus = async () => {
    setError(""); setOk("");
    try {
      await api.post(`/deposits/${id}/status`, { status, comment });
      setOk("Statut mis à jour");
      setComment("");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const runPlag = async () => {
    setError(""); setOk("");
    try {
      await api.post(`/deposits/${id}/plagiarism/run`);
      setOk("Test anti-plagiat exécuté");
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  if (!doc) return <div className="text-slate-500">Chargement...</div>;

  const isMemoire = doc.kind === "memoire";
  return (
    <div data-testid="deposit-detail">
      <Link to="/dashboard/depots" className="text-sm text-slate-500 hover:text-unisic-blue flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Retour aux dépôts
      </Link>
      <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-sm text-slate-500">{doc.tracking_number}</div>
          <h1 className="mt-1 text-2xl font-bold text-unisic-blue max-w-3xl">{doc.subject}</h1>
        </div>
        <StatusBadge status={doc.status} />
      </div>
      <span className="divider-gold mt-3" />

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="paper-card p-6">
            <div className="eyebrow eyebrow-dark">Informations</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
              {isMemoire ? (
                <>
                  <Info label="Étudiant" value={doc.student_name} />
                  <Info label="E-mail" value={doc.student_email} />
                  <Info label="Téléphone" value={doc.phone} />
                  <Info label="Directeur" value={doc.director_name} />
                  <Info label="E-mail directeur" value={doc.director_email} />
                </>
              ) : (
                <>
                  <Info label="Groupe" value={doc.group_name} />
                  <Info label="Chef" value={doc.group_leader} />
                  <Info label="Encadreur" value={doc.supervisor_name} />
                  <Info label="E-mail encadreur" value={doc.supervisor_email} />
                  <Info label="E-mail contact" value={doc.student_email} />
                  <Info label="Téléphone" value={doc.phone} />
                </>
              )}
              <Info label="Faculté" value={doc.faculty} />
              <Info label="Département" value={doc.department} />
              <Info label="Promotion" value={doc.promotion} />
              <Info label="Année académique" value={doc.academic_year} />
            </div>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-slate-500">Résumé</div>
              <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{doc.abstract}</p>
            </div>
            <div className="mt-4">
              <div className="text-xs uppercase tracking-widest text-slate-500">Mots-clés</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(doc.keywords || "").split(",").map((k) => k.trim()).filter(Boolean).map((k, i) => (
                  <span key={i} className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-700">{k}</span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex gap-3 flex-wrap">
              {doc.file_path && (
                <>
                  <button onClick={() => setShowViewer((v) => !v)} className="btn-primary text-sm" data-testid="btn-toggle-viewer">
                    <BookOpen className="w-4 h-4" /> {showViewer ? "Masquer la liseuse" : "Lire dans la liseuse"}
                  </button>
                  <a href={fileUrl(doc.file_path)} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm" data-testid="btn-open-pdf">
                    <Download className="w-4 h-4" /> Ouvrir en plein écran
                  </a>
                </>
              )}
              {doc.annexes_path && (
                <a href={fileUrl(doc.annexes_path)} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm">
                  <Download className="w-4 h-4" /> Télécharger les annexes
                </a>
              )}
            </div>
          </div>

          {showViewer && doc.file_path && (
            <PdfViewer url={fileUrl(doc.file_path)} filename={`${doc.tracking_number || "depot"}.pdf`} />
          )}

          {/* Historique */}
          <div className="paper-card p-6">
            <div className="eyebrow eyebrow-dark">Historique</div>
            <ol className="mt-4 relative border-l-2 border-slate-200 pl-6 space-y-5">
              {(doc.history || []).map((h, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-unisic-gold" />
                  <div className="flex items-center gap-3 flex-wrap">
                    <StatusBadge status={h.status} />
                    <span className="text-xs text-slate-500">{new Date(h.at).toLocaleString("fr-FR")}</span>
                    {h.by && <span className="text-xs text-slate-500">· {h.by}</span>}
                  </div>
                  {h.comment && <p className="mt-1 text-sm text-slate-600">{h.comment}</p>}
                </li>
              ))}
            </ol>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="paper-card p-6">
            <div className="eyebrow eyebrow-dark">Mettre à jour le statut</div>
            <div className="mt-4 space-y-3">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="select-new-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (facultatif)" className="w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="input-status-comment" />
              <button onClick={saveStatus} className="btn-primary text-sm w-full justify-center" data-testid="btn-save-status">
                <Save className="w-4 h-4" /> Enregistrer
              </button>
              {ok && <div className="text-xs text-green-700">{ok}</div>}
              {error && <div className="text-xs text-unisic-red">{error}</div>}
            </div>
          </div>

          <div className="paper-card p-6">
            <div className="eyebrow eyebrow-dark">Anti-plagiat</div>
            <p className="mt-2 text-xs text-slate-500">
              Architecture prête à intégrer Compilatio / Turnitin. Un test simulé permet de valider le flux.
            </p>
            {doc.plagiarism ? (
              <div className="mt-3 text-sm">
                Similarité : <span className="font-semibold text-unisic-blue">{doc.plagiarism.similarity}%</span><br />
                Décision : <span className="font-semibold text-unisic-blue">{doc.plagiarism.decision}</span>
              </div>
            ) : <div className="mt-3 text-xs text-slate-500">Aucun test exécuté.</div>}
            <button onClick={runPlag} className="mt-4 btn-secondary text-sm w-full justify-center" data-testid="btn-run-plag">
              <Play className="w-4 h-4" /> Lancer le test anti-plagiat
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-800">{value || "—"}</div>
    </div>
  );
}
